﻿import * as _ from "lodash";
import * as Bowser from "bowser"; // To extract information of the UserAgent header
import $ from "jquery";
import { IBrowserInfo } from './IBrowserInfo';
import { IDelayedAction } from "./IDelayedAction";
import { IOpenGL } from "./IOpenGL";
import { IUserAction } from "./IUserAction";
import { IUserActionClose } from "./IUserActionClose";
import { openGL } from "./OpenGL/OpenGL";
import { OpenGLParser } from "./OpenGL/parser";

// eslint-disable-next-line @typescript-eslint/no-extraneous-class
export class UserAnalytics {
    private static _source: string = null;

    private static _url: string = null;

    private static _count = 0;

    private static readonly _maxCount = 100;

    private static _throttledPost: (isUnloading?: number) => void;

    private static _actionList: Array<IUserAction | IUserActionClose>;

    private static _sessionId: number = null;

    private static _pageLoadActionId: number = null;

    private static _numberOfClicks: number;

    private static _openglInfo: IOpenGL = null;

    private static _browserInfo: IBrowserInfo = null;

    /**
     * Initialize the SDK. This can be called when SDK is going to be use. This is different to call
     * _startSession.
     * With the initialization the developer is asking the following questions to the SDK:
     *
     * "What am I monitoring?" -> smartTool
     * "Where do I send the data?" -> _url
     * "Wich period do I use for send the information?" -> framePeriod
     *
     * @param source The tool that is using the SDK
     * @param url Where to send all the information (the UserAnalytics backend)
     * @param framePeriod All actions are queued to prevent from client a lot of outcomming messages. This period
     *                    tells the SDK where to send all the queued frames into a unique POST message.
     */
    public static initialize(source: string, url: string, sessionStartUtcTimestamp: number, framePeriod = 30000): void {
        if (!source) {
            console.error(`UserAnalytics initialization error. Source cannot be null.`);
        }
        UserAnalytics._source = source;
        UserAnalytics._url = url;
        UserAnalytics._actionList = new Array<IUserAction | IUserActionClose>();
        UserAnalytics._throttledPost = _.throttle(UserAnalytics._dequeueAction, framePeriod); // Let action be sent within a period of time
        UserAnalytics._numberOfClicks = 0;

        // WebGL and OpenGL information
        const openGlInfo: OpenGLParser = openGL.getParser();

        UserAnalytics._openglInfo = openGlInfo ? openGlInfo.getVertexShaderInfo() : null;

        // Load Bowser to get browser, hardware and software information of the user
        const browser = Bowser.getParser(window.navigator.userAgent);

        UserAnalytics._browserInfo = {
            browser: browser.getBrowserName() ? browser.getBrowserName() : null,
            browserVersion: browser.getBrowserVersion() ? browser.getBrowserVersion() : null,
            os: browser.getOSName() ? browser.getOSName() : null,
            osVersion: browser.getOSVersion() ? browser.getOSVersion() : null,
            platformType: browser.getPlatformType() ? browser.getPlatformType() : null,
            vendor: browser.parsePlatform() ? browser.parsePlatform().vendor : null
        };

        // This part works in Firefox, Chrome, IE and Edge. Not in Opera.
        // Send all stored actions before: close the tab, close the browser, go back in the history.
        $(window).on(`beforeunload`, UserAnalytics._onBeforeUnload);
        $(window).on(`unload`, UserAnalytics._onBeforeUnload);

        UserAnalytics._startSession(sessionStartUtcTimestamp);
        UserAnalytics._startPageLoad(sessionStartUtcTimestamp);
    }

    /**
     * Mark the end of the page load.
     * */
    public static onPageLoaded(): void {
        if (!UserAnalytics._source) {
            console.error(`UserAnalytics not initialized`);
        }
        if (!UserAnalytics._sessionId) {
            console.error(`UserAnalytics session initialized`);
        }
        if (UserAnalytics._pageLoadActionId) {
            UserAnalytics._onActionFinished(UserAnalytics._pageLoadActionId, `success`);
        }
    }

    /**
     * Stores the number of clicks.
     * */
    public static onClickAction(): void {
        if (!UserAnalytics._source) {
            console.error(`UserAnalytics not initialized`);
        }
        if (!UserAnalytics._sessionId) {
            console.error(`UserAnalytics session initialized`);
        }
        UserAnalytics._numberOfClicks++;
    }

    /**
     * Enqueue an action in the SDK. SDK is going to send it in the next frame period, if it's not the first update from the SDK,
     * otherwise, it has to wait untel next framePeriod.
     *
     * @param action Any interaction performed by an user in our smart tools
     * @param actionTarget Target of the action, who is, in a more specific way, triggering the action.
     *                     A white string action-target means the main _source is triggering the action
     * @param data Extra information to clarify some actions
     *
     * @returns The ActionID of the action, it's the unique identifier of the action.
     */
    public static onAction(actionType: string, actionTarget: string, data: string = null): number {
        if (!UserAnalytics._source) {
            console.error(`UserAnalytics not initialized`);
        }
        if (!UserAnalytics._sessionId) {
            console.error(`UserAnalytics session initialized`);
        }

        const now = new Date();

        const utcTimestamp = now.getTime();

        // Return the UUID
        return UserAnalytics._onActionInternal(utcTimestamp, actionType, actionTarget, data);
    }

    public static onDelayedAction(actionType: string): IDelayedAction {
        if (!UserAnalytics._source) {
            console.error(`UserAnalytics not initialized`);
        }
        if (!UserAnalytics._sessionId) {
            console.error(`UserAnalytics session initialized`);
        }

        return {
            actionType,
            utcTimestamp: new Date().getTime()
        };
    }

    public static onDelayedActionSuccess(delayedAction: IDelayedAction, actionTarget: string, data: string = null): void {
        if (!UserAnalytics._source) {
            console.error(`UserAnalytics not initialized`);
        }
        if (!UserAnalytics._sessionId) {
            console.error(`UserAnalytics session initialized`);
        }

        const actionID = UserAnalytics._onActionInternal(delayedAction.utcTimestamp, delayedAction.actionType, actionTarget, data);

        UserAnalytics.onActionSuccess(actionID);
    }

    public static onDelayedActionError(delayedAction: IDelayedAction, actionTarget: string, data: string = null): void {
        if (!UserAnalytics._source) {
            console.error(`UserAnalytics not initialized`);
        }
        if (!UserAnalytics._sessionId) {
            console.error(`UserAnalytics session initialized`);
        }

        const actionID = UserAnalytics._onActionInternal(delayedAction.utcTimestamp, delayedAction.actionType, actionTarget, data);

        UserAnalytics.onActionError(actionID);
    }

    /**
     * When an action was performanced successfully
     * @param actionId The action identifier
     */
    public static onActionSuccess(actionId: number): void {
        if (!UserAnalytics._source) {
            console.error(`UserAnalytics not initialized`);
        }
        if (!UserAnalytics._sessionId) {
            console.error(`UserAnalytics session initialized`);
        }
        if (!actionId) {
            console.error(`actionId is null`);
        }
        UserAnalytics._onActionFinished(actionId, `success`);
    }

    /**
     * When an action was performanced wrongly
     * @param actionId The action identifier
     */
    public static onActionError(actionId: number): void {
        if (!UserAnalytics._source) {
            console.error(`UserAnalytics not initialized`);
        }
        if (!UserAnalytics._sessionId) {
            console.error(`UserAnalytics session initialized`);
        }
        if (!actionId) {
            console.error(`actionId is null`);
        }
        UserAnalytics._onActionFinished(actionId, `error`);
    }

    /**
     * This function must be called when a session is considered start.
     * Periodically will send an update of the session. UPDATE: By now, SDK sends the Close Action to finish a session, no periodical update needed
     *
     * The User Analytics is not an intelligent SDK, so is up to the developer to decide where do a session start and if it's
     * necessary to monitore a session.
     *
     * Other posibilities for future: Every action is asociated with the _sessionId. Create a new table with the sessions.
     *
     * */
    private static _startSession(sessionStartUtcTimestamp: number): void {
        if (!UserAnalytics._sessionId) {
            // A white string action-target means the main _source is triggering the action
            UserAnalytics._sessionId = UserAnalytics._onActionInternal(sessionStartUtcTimestamp, `session`, ``);
        }
    }

    private static _startPageLoad(pageLoadStartUtcTimestamp: number): void {
        if (!UserAnalytics._pageLoadActionId) {
            // A white string action-target means the main _source is triggering the action
            UserAnalytics._pageLoadActionId = UserAnalytics._onActionInternal(pageLoadStartUtcTimestamp, `page_load`, ``);
        }
    }

    /**
     * This function update all the data to the User Analytics Collector when the user is unload the webpage in his browser.
     * */
    private static _onBeforeUnload(): void {
        if (UserAnalytics._sessionId) {
            UserAnalytics._onActionFinished(UserAnalytics._sessionId, `success`);
        }
        UserAnalytics._dequeueAction(true);
    }

    /**
     * Creates the click action with the number of clicks within the frame period.
     * */
    private static _onClickActionCreation(): void {
        if (UserAnalytics._numberOfClicks > 0) {
            UserAnalytics.onAction(`click`, `mouse`, UserAnalytics._numberOfClicks.toString());
            UserAnalytics._numberOfClicks = 0;
        }
    }

    /**
     * Send all the queued information to the User Analytics Collector (backend) using an HTTP POST method.
     * If an error ocurred in the server, all actions are stored again and SDK will make try in the next framePeriod time.
     *
     * @param isUnloading if the ajax request is the last request while the page is unloading.
     */
    private static _dequeueAction(isUnloading = false): void {
        UserAnalytics._onClickActionCreation();
        const actions = UserAnalytics._actionList;

        UserAnalytics._actionList = [];

        if (actions.length > 0) {
            const postUrl = `${UserAnalytics._url}/collector`;

            if (isUnloading) {
                if (navigator.sendBeacon !== undefined) {
                    navigator.sendBeacon(postUrl, new Blob([JSON.stringify(actions)], {
                        type: `text/plain; charset=utf-8`
                    }));
                }
                else if (`keepalive` in new Request(``) && window.fetch !== undefined) {
                    fetch(postUrl, {
                        body: JSON.stringify({
                            a: 1,
                            b: 2
                        }),
                        headers: {
                            // Next line is disabled because the name of the property is what it is, I can't change that
                            'Content-Type': `text/plain; charset=utf-8` // eslint-disable-line @typescript-eslint/naming-convention
                        },
                        keepalive: true,
                        method: `POST`
                    }).catch(error => {
                        console.error(`There was an error uploading the queued actions via fetch: `);
                        console.error(error);
                        UserAnalytics._actionList.push(...actions);
                        UserAnalytics._throttledPost();
                    });
                }
                else {
                    $.ajax({
                        async: false,
                        contentType: `text/plain; charset=utf-8`,
                        data: JSON.stringify(actions),
                        method: `post`,
                        url: postUrl
                    }).fail((jqXHR, textStatus, errorThrown) => {
                        console.error(`There was an error uploading the queued actions via ajax: `);
                        console.error(errorThrown);
                        UserAnalytics._actionList.push(...actions);
                        UserAnalytics._throttledPost();
                    })
                        .catch(() => console.error(`Obligatory catch`));
                }
            }
            else {
                $.ajax({
                    contentType: `text/plain; charset=utf-8`,
                    data: JSON.stringify(actions),
                    method: `post`,
                    url: postUrl
                }).fail((jqXHR, textStatus, errorThrown) => {
                    console.error(`There was an error uploading the queued actions via ajax: `);
                    console.error(errorThrown);
                    UserAnalytics._actionList.push(...actions);
                    UserAnalytics._throttledPost();
                })
                    .catch(() => console.error(`Obligatory catch`));
            }
        }
    }

    private static _onActionInternal(utcTimestamp: number, actionType: string, actionTarget: string, data: string = null): number {
        UserAnalytics._count = (UserAnalytics._count + 1) % UserAnalytics._maxCount;
        // Calculated the timestamp
        const timeZoneOffset = new Date().getTimezoneOffset(); // In minutes

        // Calculate the UUID
        const actionId = (utcTimestamp * UserAnalytics._maxCount) + UserAnalytics._count;

        // Collect context and action in a JSON
        const jsonContent: IUserAction = {
            actionId,
            actionTarget,
            actionType,
            browser: UserAnalytics._browserInfo.browser,
            browserVersion: UserAnalytics._browserInfo.browserVersion,
            data,
            maxTextureSize: UserAnalytics._openglInfo.maxTextureSize,
            maxVertexAttributes: UserAnalytics._openglInfo.maxVertexAttributes,
            maxVertexTextureImageUnits: UserAnalytics._openglInfo.maxVertexTextureImageUnits,
            maxVertexUniformVectors: UserAnalytics._openglInfo.maxVertexUniformVectors,
            os: UserAnalytics._browserInfo.os,
            osVersion: UserAnalytics._browserInfo.osVersion,
            platformType: UserAnalytics._browserInfo.platformType,
            screenHeight: window.screen.availHeight,
            screenWidth: window.screen.availWidth,
            // If there's not _sessionId, it means this is the session action, so use the actionId as _sessionId
            sessionId: UserAnalytics._sessionId ? UserAnalytics._sessionId : actionId,
            source: UserAnalytics._source,
            timeZoneOffset,
            utcTimestamp,
            vendor: UserAnalytics._browserInfo.vendor,
            windowHeight: window.outerHeight,
            windowWidth: window.outerWidth
        };

        // Queue a new HTTP POST
        UserAnalytics._actionList.push(jsonContent);
        UserAnalytics._throttledPost();

        // Return the UUID
        return actionId;
    }

    /**
     * Send an action finish to update the action duration and status
     * @param actionId The action unique id
     * @param status The status of the action, could be success or error
     */
    private static _onActionFinished(actionId: number, status: "success" | "error"): void {
        // Timestamp
        const now = new Date();

        const utcTimestamp = now.getTime();

        // Create the json
        const jsonContent: IUserActionClose = {
            actionId,
            result: status,
            utcTimestamp
        };

        // PUT a close event
        UserAnalytics._actionList.push(jsonContent);
        UserAnalytics._throttledPost(actionId);
    }
}
